import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import api from '../services/api';
import ClaudeOCRReview from './ClaudeOCRReview';
import { ExtractionProgress } from './ClaudeOCRUpload';
import ConfirmModal from './ConfirmModal';
import Pagination from './Pagination';
import Toast from './Toast';
import SortIcon from './SortIcon';

/**
 * ClaudeOCRBatchQueue - Sequential upload queue for invoice PDFs.
 *
 * Upload multiple PDFs, process them one at a time, review each, and save as invoices.
 *
 * @component
 * @param {Object} props
 * @param {string|number} [props.activeBusiness] - Currently selected business ID from App header
 */
function ClaudeOCRBatchQueue({ activeBusiness }) {
  const [queueItems, setQueueItems] = useState([]);
  const [reviewingItem, setReviewingItem] = useState(null);
  // Which item's Review button is mid-fetch (vendors + categories + preview).
  // Null when nothing is loading. Only the clicked row's Review button is
  // affected — the rest of the queue stays interactive so the user can
  // still Delete or Process other items while a review opens.
  const [reviewLoadingId, setReviewLoadingId] = useState(null);
  const [reExtracting, setReExtracting] = useState(false);
  const [justReviewedId, setJustReviewedId] = useState(null);
  const [toast, setToast] = useState(null);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [draggingOver, setDraggingOver] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [deleting, setDeleting] = useState(false);
  const [extractingId, setExtractingId] = useState(null);
  const fileInputRef = useRef(null);
  const reviewIsDirtyRef = useRef(false);

  /**
   * Load queue items from the backend.
   */
  const loadQueue = useCallback(async () => {
    try {
      const response = await api.getOCRQueue();
      setQueueItems(response.data);
    } catch (err) {
      console.error('Failed to load queue:', err);
    }
  }, []);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  /**
   * Handle batch file upload.
   * @param {FileList} files - PDF files to upload
   */
  const handleUpload = useCallback(async (files) => {
    if (!files || files.length === 0) return;

    const ACCEPTED = ['.pdf', '.jpg', '.jpeg', '.png', '.heic', '.heif', '.webp'];
    const validFiles = Array.from(files).filter(f => {
      const name = (f.name || '').toLowerCase();
      return ACCEPTED.some(ext => name.endsWith(ext));
    });
    if (validFiles.length === 0) {
      setToast({ message: 'Please select PDF or image files', type: 'error' });
      return;
    }

    setToast(null);

    try {
      const formData = new FormData();
      validFiles.forEach(f => formData.append('files', f));
      await api.uploadToQueue(formData);
      await loadQueue();
    } catch (err) {
      setToast({ message: 'Upload failed. Please try again.', type: 'error' });
    }
  }, [loadQueue]);

  /**
   * Process a single queue item with COGSLOG.
   * Updates the row status to 'extracting' while in progress.
   *
   * @param {number} queueId - Queue item ID
   */
  const pendingReviewRef = useRef(null);

  const processItem = useCallback(async (queueId) => {
    setExtractingId(queueId);
    setQueueItems(prev => prev.map(item =>
      item.id === queueId ? { ...item, processing_status: 'extracting' } : item
    ));
    // Fire the extraction request but don't await it — it may take >60s
    // and CloudFront will drop the browser connection. Instead, poll for status.
    api.processQueueItemWithVision(queueId, {}).catch(() => {});

    // Poll queue status every 5s until item is extracted, error, or 3 min timeout
    const maxAttempts = 36;
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, 5000));
      try {
        const response = await api.getOCRQueue();
        const item = response.data.find(q => q.id === queueId);
        if (item && (item.processing_status === 'extracted' || item.processing_status === 'completed')) {
          setQueueItems(response.data);
          setExtractingId(null);
          if (item.extracted_data) {
            pendingReviewRef.current = item;
          }
          return;
        }
        if (item && item.processing_status === 'error') {
          setQueueItems(response.data);
          setToast({ message: item.error_message || `Failed to process item ${queueId}`, type: 'error' });
          setExtractingId(null);
          return;
        }
      } catch {
        // Poll request failed — keep trying
      }
    }
    // Timed out after 3 minutes
    setToast({ message: 'Processing timed out. Try refreshing the page.', type: 'error' });
    setExtractingId(null);
    await loadQueue();
  }, [loadQueue]);

  /**
   * Perform the deletion of all queue items.
   * Called after user confirms via ConfirmModal.
   */
  const doDeleteAll = useCallback(async () => {
    if (queueItems.length === 0) return;

    setDeleting(true);

    for (let i = 0; i < queueItems.length; i++) {
      try {
        await api.deleteQueueItem(queueItems[i].id);
      } catch {
        // Continue deleting even if some fail
      }
    }

    setDeleting(false);
    await loadQueue();
  }, [queueItems, loadQueue]);

  /**
   * Show confirmation modal for deleting all items.
   */
  const deleteAll = useCallback(() => {
    if (queueItems.length === 0) return;

    setConfirmModal({
      title: 'Delete All Items',
      message: `Delete all ${queueItems.length} item(s) from the queue?`,
      description: 'This will permanently remove all items and their PDF files. This cannot be undone.',
      confirmLabel: 'Delete All',
      variant: 'danger',
      onConfirm: async () => {
        setConfirmModal(null);
        await doDeleteAll();
      },
    });
  }, [queueItems, doDeleteAll]);

  /**
   * Show confirmation modal for deleting a single queue item.
   * @param {number} queueId - Queue item ID
   * @param {string} [fileName] - Original filename for the confirm message
   */
  const deleteItem = useCallback((queueId, fileName) => {
    setConfirmModal({
      title: 'Delete Item',
      message: `Delete "${fileName || 'this item'}" from queue?`,
      confirmLabel: 'Delete',
      variant: 'danger',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await api.deleteQueueItem(queueId);
          await loadQueue();
        } catch {
          setToast({ message: 'Failed to delete item', type: 'error' });
        }
      },
    });
  }, [loadQueue]);

  /**
   * Delete a queue item without confirmation (used when user already chose to delete,
   * e.g. from the duplicate invoice "Delete from Queue" button).
   * @param {number} queueId - Queue item ID
   */
  const deleteItemDirect = useCallback(async (queueId) => {
    try {
      await api.deleteQueueItem(queueId);
      await loadQueue();
    } catch {
      setToast({ message: 'Failed to delete item', type: 'error' });
    }
  }, [loadQueue]);

  /**
   * Open review panel for an extracted item.
   * Fetches a preview image on-demand from the backend before showing the review.
   * Re-resolves vendor and categories against current DB state.
   *
   * @param {Object} item - Queue item with extracted data
   */
  const openReview = useCallback(async (item) => {
    if (!item.extracted_data) return;

    // Show a per-button spinner on the row we're opening. Cleared in a
    // finally-shape below so it clears on both the happy path and if any
    // fetch inside Promise.allSettled surfaces an error we handle later.
    setReviewLoadingId(item.id);

    let data;
    try {
      const parsed = typeof item.extracted_data === 'string'
        ? JSON.parse(item.extracted_data)
        : item.extracted_data;

      let lineItems = [];
      if (item.extracted_line_items) {
        lineItems = typeof item.extracted_line_items === 'string'
          ? JSON.parse(item.extracted_line_items)
          : item.extracted_line_items;
      }

      data = {
        ...parsed,
        line_items: lineItems,
        confidence: parsed.confidence || item.extraction_confidence || 'medium',
        new_categories: parsed.new_categories || [],
        misc_charges: parsed.misc_charges || [],
      };
    } catch {
      data = { vendor_name: '', line_items: [], misc_charges: [], new_categories: [] };
    }

    // Mark UAT 2026-05-27 (GH #151): parallelize the three independent
    // fetches (vendors, categories, preview) instead of awaiting them
    // sequentially. The preview fetch in particular returns a base64 image
    // and can take several seconds; serialized, the user saw a 10+ second
    // unresponsive page when clicking "Review Next". Promise.allSettled
    // so any one fetch's failure doesn't abort the others.
    const needsVendorLookup = !item.detected_vendor_id && data.vendor_name;
    const [vendorRes, catRes, previewRes] = await Promise.allSettled([
      needsVendorLookup ? api.getVendors() : Promise.resolve(null),
      api.getCategories(),
      api.getQueueItemPreview(item.id),
    ]);

    // Re-resolve vendor (now using the prefetched result)
    if (needsVendorLookup && vendorRes.status === 'fulfilled' && vendorRes.value) {
      try {
        const vendors = (vendorRes.value.data || []).filter(v => v.status === 'active');
        const vendorNameLower = data.vendor_name.toLowerCase();
        const match = vendors.find(v => vendorNameLower.includes(v.name.toLowerCase()) || v.name.toLowerCase().includes(vendorNameLower));
        if (match) {
          item = { ...item, detected_vendor_id: match.id, vendor_name: match.name };
        }
      } catch { /* non-critical */ }
    }

    // Re-resolve categories: match "new" suggestions against current DB
    try {
      if (catRes.status !== 'fulfilled') throw new Error('category fetch failed');
      const existingCats = (catRes.value.data || []).filter(c => c.status === 'active');

      /**
       * Find the best matching existing category for a suggested category.
       * Tries exact match first, then fuzzy (level1 + level3 match).
       *
       * @param {Object} sc - Suggested category with level1/level2/level3
       * @returns {Object|null} Matching existing category or null
       */
      const findCategoryMatch = (sc) => {
        if (!sc || !sc.level1) return null;
        const l1 = sc.level1, l2 = sc.level2 || null, l3 = sc.level3 || null;
        // Exact match: all 3 levels
        let match = existingCats.find(c => c.level1 === l1 && c.level2 === l2 && c.level3 === l3);
        if (match) return match;
        // Fuzzy: level1 + level3 match (level2 may differ in naming)
        if (l3) {
          match = existingCats.find(c => c.level1 === l1 && c.level3 === l3);
          if (match) return match;
        }
        // Fuzzy: level1 match + level3 contains/contained-in
        if (l3) {
          const l3lower = l3.toLowerCase();
          match = existingCats.find(c =>
            c.level1 === l1 && c.level3 &&
            (c.level3.toLowerCase().includes(l3lower) || l3lower.includes(c.level3.toLowerCase()))
          );
          if (match) return match;
        }
        return null;
      };

      // Remove new_categories that now exist in DB
      const remainingNewCats = (data.new_categories || []).filter(newCat => !findCategoryMatch(newCat));

      // Re-map every line item's suggested_category against existing categories
      data.line_items = (data.line_items || []).map(li => {
        const sc = li.suggested_category;
        if (!sc) return li;
        if (sc.category_id && !sc.is_new) return li;

        const match = findCategoryMatch(sc);
        if (match) {
          return {
            ...li,
            suggested_category: {
              ...sc,
              category_id: match.id,
              is_new: false,
              _newKey: undefined,
            },
          };
        }
        return li;
      });

      data.new_categories = remainingNewCats;
    } catch (err) {
      console.error('[BatchQueue] Category re-resolution failed:', err);
    }

    // Preview was prefetched in parallel above
    let previewImage = null;
    let previewPages = [];
    let previewPageUrls = [];
    if (previewRes.status === 'fulfilled' && previewRes.value) {
      previewImage = previewRes.value.data.preview_image;
      previewPages = previewRes.value.data.preview_pages || [];
      // New shape (2026-08-27): backend returns URLs for pages 2..N so the
      // browser lazy-loads them via <img src> after the review has opened
      // on page 1. Falls back to previewPages (all base64) when the
      // backend hasn't shipped the new field yet.
      previewPageUrls = previewRes.value.data.page_urls || [];
    }

    setReviewingItem({
      ...item, parsedData: data, previewImage, previewPages, previewPageUrls,
    });
    // Clear the row spinner. setReviewingItem triggers the route change to
    // the review view anyway, but clearing this now means the queue row is
    // in a clean state when the user comes back.
    setReviewLoadingId(null);
  }, []);

  // Auto-open review after processItem completes
  useEffect(() => {
    if (pendingReviewRef.current && !extractingId) {
      const item = pendingReviewRef.current;
      pendingReviewRef.current = null;
      openReview(item);
    }
  }, [extractingId, openReview]);

  /**
   * Find the next reviewable queue item after the given ID.
   * Looks for items with status "extracted", excluding the given ID.
   *
   * @param {number} excludeId - Item ID to skip (the one just reviewed)
   * @param {Array} items - Queue items to search through
   * @returns {Object|null} Next reviewable item or null
   */
  const findNextReviewable = useCallback((excludeId, items) => {
    return items.find(
      item => item.id !== excludeId &&
        item.processing_status === 'extracted' &&
        item.extracted_data
    ) || null;
  }, []);

  /**
   * Handle returning from review. Sets justReviewedId for row highlighting,
   * reloads the queue, and clears the highlight after 2 seconds.
   *
   * @param {number} reviewedItemId - The ID of the item that was just reviewed
   */
  const closeReview = useCallback(async (reviewedItemId) => {
    setReviewingItem(null);
    if (reviewedItemId) {
      setJustReviewedId(reviewedItemId);
      setTimeout(() => setJustReviewedId(null), 2000);
    }
    await loadQueue();
  }, [loadQueue]);

  /**
   * Open the next reviewable item after saving.
   * Refreshes the queue from the API to get current statuses, then finds next.
   *
   * @param {number} currentItemId - The item just saved
   */
  const reviewNext = useCallback(async (currentItemId) => {
    const response = await api.getOCRQueue();
    setQueueItems(response.data);
    const next = findNextReviewable(currentItemId, response.data);
    if (next) {
      openReview(next);
    } else {
      setReviewingItem(null);
      setToast(null);
    }
  }, [findNextReviewable, openReview]);

  /**
   * Open the previous reviewable item relative to the current one.
   * @param {number} currentItemId - The item currently being reviewed
   */
  const reviewPrevious = useCallback(async (currentItemId) => {
    const response = await api.getOCRQueue();
    setQueueItems(response.data);
    const reviewable = response.data.filter(i =>
      i.processing_status === 'extracted' && i.extracted_data
    );
    const currentIdx = reviewable.findIndex(i => i.id === currentItemId);
    const prevIdx = currentIdx > 0 ? currentIdx - 1 : reviewable.length - 1;
    if (reviewable.length > 1 && prevIdx !== currentIdx) {
      openReview(reviewable[prevIdx]);
    }
  }, [openReview]);

  /**
   * Toggle sort on a queue table column. Cycles: asc -> desc -> none.
   * @param {string} column - Column key to sort by
   */
  const handleSort = useCallback((column) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') setSortDirection('desc');
      else { setSortColumn(null); setSortDirection('asc'); }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  }, [sortColumn, sortDirection]);

  /**
   * Get the icon for a processing status.
   * @param {string} status - Processing status
   * @returns {string} Unicode icon character
   */
  const getStatusIcon = (status) => {
    const icons = {
      pending: '\u23F1',
      extracting: '\u2699',
      extracted: '\u2713',
      verified: '\u2713',
      completed: '\u2713',
      invoiced: '\uD83D\uDCC4',
      error: '\u2717',
    };
    return icons[status] || '\u23F1';
  };

  /**
   * Returns the CSS class name for a given processing status.
   *
   * @param {string} status - Processing status
   * @returns {string} CSS class name for the status badge
   */
  const getStatusClass = (status) => {
    const classes = {
      pending: 'badge status-badge--pending',
      extracting: 'badge status-badge--extracting',
      extracted: 'badge status-badge--extracted',
      verified: 'badge status-badge--extracted',
      completed: 'badge status-badge--completed',
      invoiced: 'badge status-badge--invoiced',
      error: 'badge status-badge--error',
    };
    return classes[status] || 'badge status-badge--pending';
  };

  /**
   * Sorted queue items based on current sort column and direction.
   */
  const sortedItems = useMemo(() => {
    if (!sortColumn) return queueItems;
    const valMap = { file: 'original_name', vendor: 'vendor_name', status: 'processing_status', confidence: 'extraction_confidence' };
    return [...queueItems].sort((a, b) => {
      const aVal = (a[valMap[sortColumn]] || '').toString().toLowerCase();
      const bVal = (b[valMap[sortColumn]] || '').toString().toLowerCase();
      const cmp = aVal.localeCompare(bVal);
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [queueItems, sortColumn, sortDirection]);

  const totalPages = Math.ceil(sortedItems.length / perPage);
  const paginatedItems = sortedItems.slice((currentPage - 1) * perPage, currentPage * perPage);

  // Auto-process: when exactly 1 file is pending and no others exist, process it automatically
  const autoProcessedRef = useRef(new Set());
  useEffect(() => {
    if (
      queueItems.length === 1 &&
      queueItems[0].processing_status === 'pending' &&
      !autoProcessedRef.current.has(queueItems[0].id)
    ) {
      autoProcessedRef.current.add(queueItems[0].id);
      processItem(queueItems[0].id);
    }
  }, [queueItems, processItem]);

  // If reviewing an item, show the review component
  if (reviewingItem) {
    const nextReviewable = findNextReviewable(reviewingItem.id, queueItems);
    const reviewableItems = queueItems.filter(i =>
      i.processing_status === 'extracted' && i.extracted_data
    );
    const currentIndex = reviewableItems.findIndex(i => i.id === reviewingItem.id) + 1;

    return (
      <div>
        <div className="ocr-batch__review-header">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => {
              if (reviewIsDirtyRef.current) {
                setConfirmModal({
                  title: 'Unsaved Changes',
                  message: 'You have unsaved changes. Discard and go back?',
                  confirmLabel: 'Discard',
                  variant: 'danger',
                  onConfirm: () => { setConfirmModal(null); closeReview(reviewingItem.id); },
                });
                return;
              }
              closeReview(reviewingItem.id);
            }}
          >
            Back to Queue
          </button>
          {reviewableItems.length > 1 && (
            <>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => reviewPrevious(reviewingItem.id)}
                title="Review previous item"
              >
                &larr; Prev
              </button>
              <span className="ocr-batch__review-counter">
                {currentIndex} of {reviewableItems.length}
              </span>
            </>
          )}
          {nextReviewable ? (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => reviewNext(reviewingItem.id)}
            >
              Review Next
            </button>
          ) : (
            <span className="ocr-batch__review-complete">
              All items reviewed
            </span>
          )}
        </div>
        <ClaudeOCRReview
          key={reviewingItem.id}
          extractedData={reviewingItem.parsedData}
          filepath={reviewingItem.filepath}
          previewImage={reviewingItem.previewImage}
          previewPages={reviewingItem.previewPages}
          previewPageUrls={reviewingItem.previewPageUrls}
          vendorId={reviewingItem.detected_vendor_id}
          vendorName={reviewingItem.vendor_name}
          activeBusiness={activeBusiness}
          queueItemId={reviewingItem.id}
          queueFilename={reviewingItem.filename}
          imageQualityWarning={reviewingItem.image_quality_warning}
          reExtracting={reExtracting}
          onDeleteFromQueue={async () => {
            await deleteItemDirect(reviewingItem.id);
            await reviewNext(reviewingItem.id);
          }}
          onDirtyChange={(dirty) => { reviewIsDirtyRef.current = dirty; }}
          onBack={() => closeReview(reviewingItem.id)}
          onReviewNext={() => reviewNext(reviewingItem.id)}
          onCloseReview={() => closeReview(reviewingItem.id)}
          onReExtract={() => {
            const doReExtract = async () => {
              setReExtracting(true);
              try {
                await processItem(reviewingItem.id);
                // CRITICAL: Fetch fresh data from API, not stale queueItems state
                const response = await api.getOCRQueue();
                const updated = response.data.find(q => q.id === reviewingItem.id);
                if (updated && updated.extracted_data) {
                  openReview(updated);
                } else {
                  setReviewingItem(null);
                }
                setQueueItems(response.data);
              } finally {
                setReExtracting(false);
              }
            };
            if (reviewIsDirtyRef.current) {
              setConfirmModal({
                title: 'Discard Edits',
                message: 'Re-extracting will discard your manual edits. Continue?',
                confirmLabel: 'Re-extract',
                variant: 'primary',
                onConfirm: () => { setConfirmModal(null); doReExtract(); },
              });
              return;
            }
            doReExtract();
          }}
        />
        {confirmModal && (
          <ConfirmModal
            isOpen={true}
            title={confirmModal.title}
            message={confirmModal.message}
            description={confirmModal.description}
            confirmLabel={confirmModal.confirmLabel}
            cancelLabel={confirmModal.cancelLabel}
            variant={confirmModal.variant}
            onConfirm={confirmModal.onConfirm}
            onCancel={() => setConfirmModal(null)}
          />
        )}
      </div>
    );
  }

  const pendingCount = queueItems.filter(i => i.processing_status === 'pending').length;
  const extractedCount = queueItems.filter(i => i.processing_status === 'extracted' || i.processing_status === 'completed').length;

  /**
   * Extract just the filename from a relative path for display.
   * @param {string} name - Full relative path (e.g., "Vendor/Vendor/file.pdf")
   * @returns {string} Just the filename portion
   */
  const displayFileName = (name) => {
    if (!name) return '';
    const parts = name.split('/');
    return parts[parts.length - 1];
  };

  return (
    <div>
      {/* Full-screen dimmed overlay shown while a Review click is being
          prepared (vendors + categories + preview all in flight). Sits
          above the whole page so a click reads as "something is
          happening" without shifting any button width. Auto-clears when
          openReview finishes and transitions to the review view. */}
      {reviewLoadingId !== null && (
        <div
          className="ocr-batch__review-loading-overlay"
          role="status"
          aria-live="polite"
          aria-label="Loading review"
        >
          <div className="ocr-batch__review-loading-panel">
            <img
              src="/logos/loader/cogslog-loader-dark-lg.svg"
              width="72"
              height="162"
              alt=""
              aria-hidden="true"
            />
            <p className="ocr-batch__review-loading-text">Loading review…</p>
          </div>
        </div>
      )}
      {/* Upload Zone */}
      <div
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click(); } }}
        onDragOver={(e) => { e.preventDefault(); setDraggingOver(true); }}
        onDragLeave={() => setDraggingOver(false)}
        onDrop={(e) => { e.preventDefault(); setDraggingOver(false); handleUpload(e.dataTransfer.files); }}
        className={`ocr-batch__upload-zone${draggingOver ? ' ocr-batch__upload-zone--dragging' : ''}`}
        role="button"
        tabIndex={0}
        aria-label="Upload PDFs or images for batch processing"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp"
          multiple
          onChange={e => handleUpload(e.target.files)}
          className="sr-only"
          aria-hidden="true"
        />
        <p className="ocr-batch__upload-heading">
          Drop PDF or image here or click to browse
        </p>
        <p className="ocr-batch__upload-subtext">
          Upload one or multiple invoices. COGSLOG will extract all invoice data.
        </p>
      </div>

      {/* Tips Section */}
      {queueItems.length === 0 && (
        <div className="card" style={{ marginTop: 'var(--sp4)', backgroundColor: 'var(--st100)' }}>
          <h3 style={{ margin: '0 0 var(--sp2)', fontSize: '0.95rem' }}>Tips for best results</h3>
          <ul style={{ margin: 0, paddingLeft: 'var(--sp4)', color: 'var(--st600)', fontSize: '0.85rem', lineHeight: '1.6' }}>
            <li>Use clear, high-resolution PDF scans for accurate extraction</li>
            <li>COGSLOG works best with standard invoice formats (line items, totals, dates)</li>
            <li>After extraction, review and edit any fields before saving</li>
            <li>Set up your vendors in <Link to="../setup" style={{ color: 'var(--g500)' }}>Setup</Link> first for automatic vendor detection</li>
          </ul>
        </div>
      )}

      {/* Toast Notifications */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Action Bar */}
      <div className="settings-form__table-header">
        <span className="ocr-batch__action-summary">
          {queueItems.length} total | {extractedCount} extracted | {pendingCount} pending
        </span>
        <div className="settings-form__table-actions">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ color: 'var(--error)' }}
            onClick={deleteAll}
            disabled={deleting || !!extractingId || queueItems.length === 0}
          >
            Delete All
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={loadQueue}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Extraction progress — above table so always visible */}
      {extractingId && (
        <div ref={el => { if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }} className="card" style={{ marginBottom: 'var(--sp3)', textAlign: 'center', padding: 'var(--sp5)' }}>
          <img src="/logos/loader/cogslog-loader-light-md.svg" width="48" height="108" alt="Loading..." role="status" style={{ display: 'block', margin: '0 auto var(--sp3)' }} />
          <p style={{ fontWeight: 600, color: 'var(--st900)', marginBottom: 'var(--sp3)' }}>
            Extracting: {(() => { const item = queueItems.find(q => q.id === extractingId); return item?.original_name || item?.filename || ''; })()}
          </p>
          <ExtractionProgress key={extractingId} />
        </div>
      )}

      {/* Queue Table */}
      <div className="tbl-wrap tbl-wrap--lg">
        <table className="ocr-batch__queue-table">
          <thead>
            <tr>
              {[
                { key: 'file', label: 'File' },
                { key: 'vendor', label: 'Vendor' },
                { key: 'status', label: 'Status' },
                { key: 'confidence', label: 'Confidence' },
                { key: null, label: 'Actions' },
              ].map(col => (
                <th
                  key={col.label}
                  scope="col"
                  onClick={col.key ? () => handleSort(col.key) : undefined}
                  className={col.key ? 'ocr-batch__th-sortable' : undefined}
                  title={col.key ? `Sort by ${col.label}` : undefined}
                  aria-sort={col.key && sortColumn === col.key ? (sortDirection === 'asc' ? 'ascending' : 'descending') : col.key ? 'none' : undefined}
                >
                  {col.label}
                  {col.key && sortColumn === col.key && (
                    <SortIcon direction={sortDirection} />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {queueItems.length === 0 ? null : (
              paginatedItems.map(item => (
                <tr key={item.id} className={justReviewedId === item.id ? 'ocr-batch__row-highlight' : undefined}>
                  <td>
                    <span title={item.original_name}>{displayFileName(item.original_name || item.filename)}</span>
                  </td>
                  <td>
                    {item.vendor_name || <span className="ocr-batch__vendor-unknown">Unknown</span>}
                  </td>
                  <td>
                    <span
                      className={getStatusClass(item.processing_status)}
                      title={item.processing_status === 'error' ? (item.error_message || 'Processing failed') : undefined}
                    >
                      {getStatusIcon(item.processing_status)} {item.processing_status}
                    </span>
                    {item.processing_status === 'error' && item.error_message && (
                      <div className="ocr-batch__error-message">
                        {item.error_message}
                      </div>
                    )}
                  </td>
                  <td>
                    {item.extraction_confidence || '-'}
                  </td>
                  <td>
                    <div className="ocr-batch__queue-actions">
                      {(item.processing_status === 'pending' || item.processing_status === 'error') && (
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => processItem(item.id)}
                          disabled={!!extractingId}
                        >
                          Process
                        </button>
                      )}
                      {item.processing_status === 'extracting' && (
                        <span className="ocr-batch__extracting-indicator" aria-live="polite">
                          <img src="/logos/loader/cogslog-loader-light-sm.svg" width="10" height="22" alt="" aria-hidden="true" />
                          Extracting
                        </span>
                      )}
                      {(item.processing_status === 'extracted' || item.processing_status === 'completed') && (
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={() => openReview(item)}
                          disabled={reviewLoadingId === item.id}
                        >
                          Review
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={{ color: 'var(--error)' }}
                        onClick={() => deleteItem(item.id, item.original_name)}
                        disabled={!!extractingId}
                        title="Delete"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {queueItems.length === 0 && (
        <div className="empty-state">
          <p className="empty-state__title">No items in queue</p>
          <p className="empty-state__description">Upload invoice PDFs above to get started.</p>
        </div>
      )}
      {sortedItems.length > perPage && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          perPage={perPage}
          onPerPageChange={(val) => { setPerPage(val); setCurrentPage(1); }}
          totalItems={sortedItems.length}
        />
      )}
      {confirmModal && (
        <ConfirmModal
          isOpen={true}
          title={confirmModal.title}
          message={confirmModal.message}
          description={confirmModal.description}
          confirmLabel={confirmModal.confirmLabel}
          cancelLabel={confirmModal.cancelLabel}
          variant={confirmModal.variant}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(null)}
        />
      )}
    </div>
  );
}


ClaudeOCRBatchQueue.propTypes = {
  /** Currently selected business ID from App header */
  activeBusiness: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ClaudeOCRBatchQueue;
